/**
 * Reading and driving the slide position of a built Slidev deck inside our
 * iframe.
 *
 * The decks are built with `--router-mode hash` (see presentations/build.sh) and
 * served from the same origin as the portal, which is what makes any of this
 * possible: we can touch `contentWindow` directly instead of inventing a
 * postMessage protocol and rebuilding nineteen decks to answer it.
 *
 * A route here is the part after the `#`. Measured against the real built decks
 * (Chrome over CDP, walking a deck with the "next" control and reading
 * `location.hash` at every step), there are exactly two forms:
 *
 *     /7              slide 7
 *     /7?clicks=3     slide 7 with three `v-click` reveals showing
 *
 * Carrying the query half matters — eight of the nineteen decks use `v-click`,
 * two of them over a hundred times, so syncing only the slide number would drop
 * followers onto an unrevealed slide and leave them there for the whole build.
 *
 * `/7/2` is NOT a click-step, however plausible it looks: the deck's router has
 * no two-segment route and renders its own 404 ("Page /7/2 not found"). Driving a
 * follower there replaces their deck with an error page.
 */

/** An iframe's `contentWindow`, with the constructors the DOM lib hides on `Window`. */
export type FrameWindow = Window & typeof globalThis

/**
 * The only two shapes a real deck produces. Deliberately strict: anything this
 * doesn't recognise is dropped rather than guessed at, because the consequence of
 * a wrong route is every follower staring at a 404.
 *
 * Slides are 1-indexed, hence the leading `[1-9]` — `/0` is not a slide, and
 * without that it would sail through here and 404 the whole room. Click counts
 * may legitimately be 0.
 */
const ROUTE = /^\/[1-9][0-9]{0,3}(\?clicks=[0-9]{1,4})?$/

/**
 * Reject anything that isn't a numeric Slidev route.
 *
 * This runs on every value that arrives from another client before it reaches
 * `pushState`. The database constrains the same shape (0020) — the point of
 * checking twice is that presence state is never validated by anything: it goes
 * client → Realtime server → client with no schema in between, so a hand-rolled
 * client could publish whatever it liked into nineteen teammates' browsers. This
 * is the only check standing in front of the live path.
 */
export function sanitizeRoute(value: string | null | undefined): string | null {
  if (!value) return null
  const route = value.startsWith('#') ? value.slice(1) : value
  return ROUTE.test(route) ? route : null
}

/** The slide number out of a route, for display and for storing alongside it. */
export function slideOf(route: string | null): number | null {
  const safe = sanitizeRoute(route)
  if (!safe) return null
  // Split on the query first: `/7?clicks=3` is slide 7, not slide NaN.
  const n = Number.parseInt(safe.slice(1).split('?')[0], 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Where the deck currently is, or null while it's still loading.
 *
 * Guarded because `contentWindow.location` throws on a cross-origin document,
 * and the frame is briefly `about:blank` before the deck's own document
 * replaces it.
 */
export function readRoute(win: FrameWindow | null | undefined): string | null {
  if (!win) return null
  try {
    return sanitizeRoute(win.location.hash)
  } catch {
    return null
  }
}

/**
 * Move a follower's deck to `route`.
 *
 * **Why the explicit `popstate` rather than `win.location.hash = route`:** the
 * deck's router listens for `popstate` and nothing else — `hashchange` appears
 * nowhere in the built bundle. Assigning `location.hash` does in fact work in
 * Chrome, because a fragment navigation fires `popstate` there too, but that is
 * the browser being generous rather than the deck being asked properly, and it's
 * a thin thing to hang a room full of screens on.
 *
 * So: write the URL through the history API and then hand the router the event it
 * is actually waiting for. Constructed from the frame's own realm, so it isn't a
 * foreign object to the listener inside it.
 *
 * Both paths were verified against a real deck in headless Chrome, along with the
 * fact that `/7?clicks=3` round-trips and `/7/2` renders a 404. If Slidev is
 * upgraded, that check is the one to repeat.
 *
 * `pushState` rather than `replaceState` because it's the verified path; the cost
 * is that a long meeting leaves a Back-button trail through the slides, which is
 * also what Slidev's own navigation does.
 */
export function driveTo(win: FrameWindow | null | undefined, route: string | null): boolean {
  const safe = sanitizeRoute(route)
  if (!win || !safe) return false
  try {
    // Already there. Skipping is not just an optimisation — every call would
    // otherwise push a history entry, and a follower's Back button would have to
    // walk through every slide change of the whole meeting.
    if (readRoute(win) === safe) return false
    win.history.pushState(null, '', `#${safe}`)
    win.dispatchEvent(new win.PopStateEvent('popstate', { state: null }))
    return true
  } catch {
    return false
  }
}
